import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  isFeedAndPubCode,
  isNonFeedAndPubCode,
  filterCodesTree,
  mapFromCodesTree, consistsOfEnglish, containsEnglish, walkCodesTree, codeIs,
} from '@/lib/codes'
import { useProfileCodesStore } from './profileCodes'
import { useUserProfileStore } from '@/ContextTab/stores/userProfile'
import { apiClientV2 } from '@/ContextTab/services/apiClient'
import type { CodeWithCategoryAndChildren } from '@/api/generated/v2'


export const useCodesStore = defineStore('codes', () => {

  const codeTree = ref<CodeWithCategoryAndChildren | null>(null)

  const profileStore = useUserProfileStore()
  const profileCodesStore = useProfileCodesStore()

  async function fetchCodes() {
    const response = await apiClientV2.CodesService.codesCodesGet()

    codeTree.value = !response.error && response.data
      ? response.data
      : null

    return response
  }

  const actualCodes = computed<CodeWithCategoryAndChildren[]>(() =>
    codeTree.value
      ? filterCodesTree(
          codeTree.value.children,
          (item) => !item.is_deprecated,
          false,
        )
      : [],
  )

  const codesMap = computed(() => mapFromCodesTree(codeTree.value?.children))

  const feedAndPubCodes = computed<CodeWithCategoryAndChildren[]>(() =>
    filterCodesTree(actualCodes.value, isFeedAndPubCode, true),
  )

  const nonFeedAndPubCodes = computed<CodeWithCategoryAndChildren[]>(() =>
    filterCodesTree(actualCodes.value, isNonFeedAndPubCode, true),
  )

  function isCodeIdFeedAndPub(codeId: number): boolean {
    const code = codesMap[codeId]
    return code ? isFeedAndPubCode(code) : false
  }

  const codeColorsMap = computed(() => {

    function colorizeCodeTreeNode(
      code: CodeWithCategoryAndChildren,
      inheritedColor: string | null,
      colorsMap: Record<number, string>,
    ) {
      const selfColor = profileStore.explicitProfileColors.codes?.[code.id]

      if (selfColor) {
        colorsMap[code.id] = selfColor.color

        if (selfColor.includeChildren) {
          inheritedColor = selfColor.color
        }
      } else {
        if (inheritedColor) {
          colorsMap[code.id] = inheritedColor
        }
      }

      code.children?.forEach((code) => {
        colorizeCodeTreeNode(code, inheritedColor, colorsMap)
      })
    }

    // we set colors for codes separately for feed and non-feed code trees,
    // but both trees are extracted from all codes tree, so we just use it
    // to avoid unnecessary checks like `if (feed) searchInFeeColors else...`

    const colorsMap: Record<number, string> = {}
    actualCodes.value.forEach((code) => colorizeCodeTreeNode(code, null, colorsMap))
    return colorsMap
  })

  const codesForRepublication = computed<{
    codes: Record<string, CodeWithCategoryAndChildren[]> // TODO: fix type to language name as key
    disabledIds: number[]
  }>(() => {
    if (
      !profileCodesStore.userHasPublicationCodes // -> technical pub codes only
      || !profileCodesStore.newsPublicationCodes
    ) {
      return {
        codes: {
          RU: profileCodesStore.newsPublicationCodes ?? [],
          EN: profileCodesStore.newsPublicationCodes ?? [],
        },
        disabledIds: [],
      }
    }

    const possibleCodes = [
      ...profileCodesStore.newsPublicationCodes ?? [],
      ...profileCodesStore.newsCreationCodes ?? [],
    ]

    const ruCodes = filterCodesTree(
      possibleCodes,
      (item) => !consistsOfEnglish(item),
      false,
    )

    const enCodes = filterCodesTree(
      possibleCodes,
      (item) => containsEnglish(item),
      false,
    )

    const disabledIds: number[] = []
    walkCodesTree(possibleCodes, (item, level) => {
      // the same rule as for POST /news/{id}/codes: select children only or any IPTC or ОКАТО
      if (
        level < 1
        || (
          item.children.length
          && !codeIs.iptc(item)
          && !codeIs.regional(item)
        )
      ) {
        disabledIds.push(item.id)
      }
    })

    return {
      codes: {
        RU: ruCodes,
        EN: enCodes,
      },
      disabledIds,
    }
  })

  return {
    fetchCodes,
    codeTree,
    codesMap,
    feedAndPubCodes,
    nonFeedAndPubCodes,
    codeColorsMap,
    isCodeIdFeedAndPub,
    codesForRepublication,
  }
})
